import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { ArrowLeft, User, Mail, Phone, MapPin, Calendar, Shield, CheckCircle, XCircle, FileText } from 'lucide-react';
import "../styles/Admin.css";
import { userProfile, verifyuser, userApplications } from '../ServiceAPI/AdminUser/api';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check admin authentication
    const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
    if (!isAdminAuthenticated) {
      navigate("/admin/login");
      return;
    }

    // Fetch user profile data from API
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const userData = await userProfile(userId);
        console.log('User Profile Data:', userData);

        // Handle different response structures
        let user = null;
        if (userData.user) {
          user = userData.user;
        } else if (Array.isArray(userData) && userData.length > 0) {
          user = userData[0];
        } else {
          user = userData;
        }

        setUser(user);
        setError('');
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    // Fetch user applications separately
    const fetchUserApplications = async () => {
      try {
        const appData = await userApplications(userId);
        console.log('User Applications Data:', appData);

        // Handle different response structures
        let apps = [];
        if (Array.isArray(appData)) {
          apps = appData;
        } else if (appData.applications && Array.isArray(appData.applications)) {
          apps = appData.applications;
        } else if (appData.data && Array.isArray(appData.data)) {
          apps = appData.data;
        }

        setApplications(apps);
      } catch (err) {
        console.error('Error fetching user applications:', err);
        setApplications([]);
      }
    };

    if (userId) {
      fetchUserProfile();
      fetchUserApplications();
    }
  }, [userId, navigate]);

  const toggleVerification = async () => {
    if (user) {
      try {
        const newStatus = {
          isEmailVerified: parseInt(user.isEmailVerified) === 1 ? 0 : 1,
          isPhoneVerified: parseInt(user.isPhoneVerified) === 1 ? 0 : 1,
        };

        await verifyuser(userId, newStatus);

        // Re-fetch user profile data to ensure we have the latest from DB
        const userData = await userProfile(userId);
        let updatedUser = null;
        if (userData.user) {
          updatedUser = userData.user;
        } else if (Array.isArray(userData) && userData.length > 0) {
          updatedUser = userData[0];
        } else {
          updatedUser = userData;
        }

        setUser(updatedUser);
      } catch (err) {
        console.error('Error updating verification status:', err);
        setError('Failed to update verification status');
      }
    }
  };

  if (loading) return (
    <AdminLayout>
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading user profile...</p>
      </div>
    </AdminLayout>
  );

  if (error) return (
    <AdminLayout>
      <div className="alert alert-error">{error}</div>
      <button className="btn-secondary-modern" onClick={() => navigate('/admin/users')}>
        <ArrowLeft size={18} />
        Back to Users
      </button>
    </AdminLayout>
  );

  if (!user) return (
    <AdminLayout>
      <div className="alert alert-error">User not found</div>
      <button className="btn-secondary-modern" onClick={() => navigate('/admin/users')}>
        <ArrowLeft size={18} />
        Back to Users
      </button>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/admin/users')}>
            <ArrowLeft size={18} />
            Back to Users
          </button>
          <h1 className="admin-page-title">User Profile</h1>
          <p className="admin-page-subtitle">{user.Username || user.name || 'User Details'}</p>
        </div>
      </div>

      <div className="admin-content-area">
        <div className="details-layout">
          {/* User Header Card */}
          <div className="user-profile-card">
            <div className="user-profile-header-modern">
              <div className="user-avatar-large">
                {(user.Username || user.name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="user-profile-info">
                <h2>{user.Username || user.name || 'Unknown User'}</h2>
                <p className="user-profile-email">
                  <Mail size={16} />
                  {user.Email || 'No email provided'}
                </p>
                <div className="user-profile-badges">
                  <span className={`status-badge-modern ${parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1 ? 'approved' : 'unverified'
                    }`}>
                    {parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1 ? 'Verified User' : 'Unverified User'}
                  </span>
                </div>
              </div>
              <button
                className={`verification-btn-modern ${parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1 ? 'unverify' : 'verify'
                  }`}
                onClick={toggleVerification}
              >
                {parseInt(user.isEmailVerified) === 1 && parseInt(user.isPhoneVerified) === 1 ? (
                  <>
                    <XCircle size={18} />
                    Unverify User
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Verify User
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Personal Information Card */}
          <div className="details-card">
            <div className="details-card-header">
              <User size={24} />
              <h2>Personal Information</h2>
            </div>
            <div className="details-grid-modern">
              <div className="detail-item-modern">
                <label><Mail size={16} /> Email</label>
                <p>{user.Email || 'Not provided'}</p>
              </div>
              <div className="detail-item-modern">
                <label><Phone size={16} /> Phone</label>
                <p>{user.Phone || 'Not provided'}</p>
              </div>
              <div className="detail-item-modern">
                <label><Calendar size={16} /> Date of Birth</label>
                <p>{user.DOB ? new Date(user.DOB).toLocaleDateString() : 'Not provided'}</p>
              </div>
              <div className="detail-item-modern">
                <label><MapPin size={16} /> State</label>
                <p>{user.StateName || 'Not provided'}</p>
              </div>
              {user.Address && (
                <div className="detail-item-modern full-width">
                  <label><MapPin size={16} /> Address</label>
                  <p>{user.Address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Verification Status Card */}
          <div className="details-card">
            <div className="details-card-header">
              <Shield size={24} />
              <h2>Verification Status</h2>
            </div>
            <div className="details-grid-modern">
              <div className="detail-item-modern">
                <label>Email Verified</label>
                <p>
                  <span className={`status-badge-modern ${parseInt(user.isEmailVerified) === 1 ? 'approved' : 'pending'}`}>
                    {parseInt(user.isEmailVerified) === 1 ? (
                      <>
                        <CheckCircle size={14} />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle size={14} />
                        Not Verified
                      </>
                    )}
                  </span>
                </p>
              </div>
              <div className="detail-item-modern">
                <label>Phone Verified</label>
                <p>
                  <span className={`status-badge-modern ${parseInt(user.isPhoneVerified) === 1 ? 'approved' : 'pending'}`}>
                    {parseInt(user.isPhoneVerified) === 1 ? (
                      <>
                        <CheckCircle size={14} />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle size={14} />
                        Not Verified
                      </>
                    )}
                  </span>
                </p>
              </div>
              <div className="detail-item-modern">
                <label>User ID</label>
                <p><span className="td-badge">{user.userId || user.Id || user.id || userId}</span></p>
              </div>
            </div>
          </div>

          {/* Applications History Card */}
          <div className="details-card full-width">
            <div className="details-card-header">
              <FileText size={24} />
              <h2>Applications History</h2>
            </div>
            {applications && applications.length > 0 ? (
              <div className="table-responsive">
                <table className="modern-table compact">
                  <thead>
                    <tr>
                      <th>Application ID</th>
                      <th>Service Name</th>
                      <th>Service Type</th>
                      <th>Service Category</th>
                      <th>Status</th>
                      <th>Applied Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app, index) => (
                      <tr key={app.ApplicationId || index}>
                        <td><span className="td-badge">{app.ApplicationNumber || app.ApplicationId || `APP-${index + 1}`}</span></td>
                        <td><strong>{app.ServiceName || 'Unknown'}</strong></td>
                        <td>{app.ServiceType || 'N/A'}</td>
                        <td>{app.ServiceCategory || 'N/A'}</td>
                        <td>
                          <span className={`status-badge-modern ${(app.ApplicationStatus || 'pending').toLowerCase()}`}>
                            {app.ApplicationStatus || 'Pending'}
                          </span>
                        </td>
                        <td>{app.ApplicationCreatedAt ? new Date(app.ApplicationCreatedAt).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FileText size={48} />
                <p>No applications found for this user</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserProfile;